import { useMemo } from 'react';
import './errorSummary.scss';

import { getLabelTextById } from '@src/utils/helpers';
import classNames from 'classnames';
import ErrorSummaryBase from './ErrorSummaryBase';
import { cleanAndTruncateLabels, scrollById } from './errorSummary.utils';
import { useRequest } from '@stores';
import LinkButton from '@components/link/linkButton';

const typeSuffixes = {
  modal: '_modal',
  form: 'form',
};

const renderApiValidation = (error, index) => (
  <li key={index}>
    <p className='margin-bottom-0 text-bold'>{error.sectionTitle}</p>
    <ul className='api-error-list'>
      {Object.keys(error.errors)?.map((key, index) => (
        <li key={index}>{key}</li>
      ))}
    </ul>
  </li>
);

const generateErrorDataItem = (key, errorMessage, typeSuffix, modalID) => {
  const labelText = cleanAndTruncateLabels(getLabelTextById(key), 40) ?? '';
  const onClick = () => scrollById(key, modalID, typeSuffix);
  const parts = String(errorMessage)
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  if (parts.length > 1) {
    const [main, ...subs] = parts;

    return {
      id: `${key}_li${typeSuffix}`,
      onClick,
      renderFunction: () => (
        <li key={`${key}_nested`} id={`${key}_li${typeSuffix}`}>
          <LinkButton onClick={onClick}>
            {labelText}
            {main}
          </LinkButton>
          <ul className='margin-top-1'>
            {subs.map((sub, idx) => (
              <li key={`${key}_sub_${idx}`}>{sub}</li>
            ))}
          </ul>
        </li>
      ),
    };
  }

  return {
    id: `${key}_li${typeSuffix}`,
    onClick,
    text: labelText + errorMessage,
  };
};

const ErrorSummary = ({ defaultExpanded, errors, type = 'base', modalID, sectionNo = 0 }) => {
  const sections = useRequest((state) => state.sections);
  const setActiveSection = useRequest((state) => state.setActiveSection);
  const typeSuffix = typeSuffixes[type] || sectionNo;
  const isModal = type === 'modal';
  const isForm = type === 'form';
  const isApi = type === 'api-validation';
  const errorString = errors ? Object.keys(errors).reduce((acc, key) => acc + key + errors[key]?.message, '') : '';

  const genericErrorData = useMemo(() => {
    if (!errorString || !errors || Object.keys(errors).length === 0) {
      return [];
    }
    return Object.keys(errors).reduce((acc, key) => {
      const errorMessage = errors[key].message;
      return errorMessage ? [...acc, generateErrorDataItem(key, errorMessage, typeSuffix, modalID)] : acc;
    }, []);
  }, [errorString, errors, modalID, typeSuffix]);

  const sectionErrorData = useMemo(() => {
    const finalSection = 'Certify, Sign, and Submit';

    return sections
      .map((section) => ({
        id: !section.hidden && section.valid === false ? `${section.id}_li${typeSuffix}` : null,
        onClick: () => setActiveSection(section),
        text: section.label,
        validity: section.valid,
      }))
      .filter((item) => item.id && item.text !== finalSection);
  }, [sections, setActiveSection, typeSuffix]);

  const apiErrorData = useMemo(
    () =>
      isApi
        ? errors?.map((error) => ({
            renderFunction: renderApiValidation,
            errorData: error,
          }))
        : [],
    [errorString, isApi] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const errorData = useMemo(() => {
    if (isForm) {
      // For form type, combine both section errors AND field errors
      return sectionErrorData.length > 0 ? sectionErrorData : genericErrorData;
    } else if (isApi) {
      return apiErrorData;
    } else {
      return genericErrorData;
    }
  }, [apiErrorData, genericErrorData, isApi, isForm, sectionErrorData]);

  const genericErrorCount = genericErrorData?.length;
  const sectionErrorCount = sectionErrorData?.length;
  const hasGenericErrors = genericErrorCount > 0;
  const hasSectionErrors = sectionErrorCount > 0;
  const hasFormErrors = isForm && hasSectionErrors;
  const hasApiErrors = isApi && errors?.length > 0;
  const hasErrors = hasGenericErrors || hasFormErrors || hasApiErrors;
  const summaryStyles = classNames({
    'modal-variant': isModal,
  });

  const alertTitle = useMemo(() => {
    const genericCountText = `Resolve the following ${genericErrorCount === 1 ? 'issue' : `${genericErrorCount} issues`}`;
    const sectionCountText = `This request is incomplete. Resolve the following ${sectionErrorCount === 1 ? 'issue' : `${sectionErrorCount} issues`}`;
    const sectionCompleteText = 'This section is complete';
    switch (type) {
      case 'modal':
        return hasGenericErrors ? genericCountText : null;

      case 'form':
        if (hasSectionErrors) {
          return sectionCountText;
        } else if (hasGenericErrors) {
          return genericCountText;
        } else {
          return sectionCompleteText;
        }

      case 'api-validation':
        const errorCount = errors.reduce(
          (apiCount, apiError) =>
            (apiCount += Object.keys(apiError.errors).reduce(
              (errorCount, key) => (apiError.errors[key] ? (errorCount += 1) : errorCount),
              0
            )),
          0
        );
        return errors
          ? `Resolve the following ${errorCount === 1 ? 'issue' : `${errorCount} issues`}`
          : 'This request is complete';

      default:
        return hasGenericErrors ? genericCountText : sectionCompleteText;
    }
  }, [errors, genericErrorCount, hasGenericErrors, hasSectionErrors, sectionErrorCount, type]);

  if (isModal && !hasErrors) return null; // don't display success message for modals

  return (
    <ErrorSummaryBase
      className={summaryStyles}
      defaultExpanded={defaultExpanded}
      errors={errorData}
      hasErrors={hasErrors}
      title={alertTitle}
      typeSuffix={typeSuffix}
    />
  );
};

export default ErrorSummary;
